import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import './IncomeReport.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ReportData {
  total_income: string;
  total_expenses: string;
  total_salaries: string;
  net_income: string;
}

interface IncomeAndExpensesByMonth {
  [month: string]: ReportData;
}

const IncomeAndExpensesReport: React.FC = () => {
  const [data, setData] = useState<IncomeAndExpensesByMonth>({});
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [branch, setBranch] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [totalStats, setTotalStats] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    totalSalaries: 0,
    netIncome: 0,
  });

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/fees/income/', {
        params: {
          year,
          branch,
          from_date: fromDate,
          to_date: toDate,
        },
      });
      setData(response.data.income_and_expenses_by_month);

      // Calculate totals
      const entries: ReportData[] = Object.values(response.data.income_and_expenses_by_month);
      const totals = entries.reduce(
        (acc, curr) => ({
          totalIncome: acc.totalIncome + parseFloat(curr.total_income || '0'),
          totalExpenses: acc.totalExpenses + parseFloat(curr.total_expenses || '0'),
          totalSalaries: acc.totalSalaries + parseFloat(curr.total_salaries || '0'),
          netIncome: acc.netIncome + parseFloat(curr.net_income || '0'),
        }),
        { totalIncome: 0, totalExpenses: 0, totalSalaries: 0, netIncome: 0 }
      );

      setTotalStats(totals);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [year, branch, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Chart Data
  const getIncomeExpenseChartData = () => {
    const months = Object.keys(data);
    return {
      labels: months,
      datasets: [
        {
          label: 'Dakhliga',
          data: months.map(m => parseFloat(data[m].total_income || '0')),
          backgroundColor: 'rgba(67, 233, 123, 0.8)',
          borderColor: 'rgba(67, 233, 123, 1)',
          borderWidth: 2,
        },
        {
          label: 'Kharashaadka',
          data: months.map(m => parseFloat(data[m].total_expenses || '0')),
          backgroundColor: 'rgba(255, 99, 132, 0.8)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 2,
        },
        {
          label: 'Mushaaraadka',
          data: months.map(m => parseFloat(data[m].total_salaries || '0')),
          backgroundColor: 'rgba(255, 206, 86, 0.8)',
          borderColor: 'rgba(255, 206, 86, 1)',
          borderWidth: 2,
        },
      ],
    };
  };

  const getNetIncomeChartData = () => {
    const months = Object.keys(data);
    return {
      labels: months,
      datasets: [
        {
          label: 'Dakhliga Saafi Ah',
          data: months.map(m => parseFloat(data[m].net_income || '0')),
          fill: true,
          backgroundColor: 'rgba(102, 126, 234, 0.2)',
          borderColor: 'rgb(102, 126, 234)',
          tension: 0.4,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePDFDownload = () => {
    const doc = new jsPDF();
    doc.text(`Warbixinta Dakhliga iyo Kharashaadka - ${year}`, 14, 10);
    doc.autoTable({
      head: [['Bisha', 'Wadarta Dakhliga', 'Wadarta Kharashaadka', 'Wadarta Mushaarka', 'Dakhliga Saafi Ah']],
      body: Object.entries(data).map(([month, report]) => [
        month,
        `KSh ${report.total_income}`,
        `KSh ${report.total_expenses}`,
        `KSh ${report.total_salaries}`,
        `KSh ${report.net_income}`,
      ]),
      startY: 20,
    });
    doc.save(`income_expenses_report_${year}.pdf`);
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      Object.entries(data).map(([month, report]) => ({
        Bisha: month,
        'Wadarta Dakhliga': report.total_income,
        'Wadarta Kharashaadka': report.total_expenses,
        'Wadarta Mushaarka': report.total_salaries,
        'Dakhliga Saafi Ah': report.net_income,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Warbixinta Dakhliga');
    XLSX.writeFile(workbook, `income_expenses_report_${year}.xlsx`);
  };

  return (
    <div className="income-report-container">
      {/* Hero Header */}
      <div className="report-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="icon">💵</span> Warbixinta Dakhliga & Kharashaadka
          </h1>
          <p className="hero-subtitle">Guudmar maaliyadeed iyo falanqayn waxqabad</p>
        </div>
      </div>

      {/* Filters Card */}
      <div className="filters-card">
        <h3 className="filters-title">
          <span className="icon-small">🔍</span> Shaandhee Warbixinta
        </h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Sannadka</label>
            <input
              type="number"
              className="filter-input"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              placeholder="Geli sannad"
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Laanta (Ikhtiyaari)</label>
            <input
              type="text"
              className="filter-input"
              value={branch || ''}
              onChange={(e) => setBranch(e.target.value || null)}
              placeholder="Geli magaca laanta"
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Laga Bilaabo</label>
            <input
              type="date"
              className="filter-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Ilaa</label>
            <input
              type="date"
              className="filter-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button className="refresh-btn" onClick={fetchData}>
            🔄 Cusbooneysii Xogta
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <button className="action-btn btn-print" onClick={handlePrint}>
          <span className="btn-icon">🖨️</span> Daabac
        </button>
        <button className="action-btn btn-pdf" onClick={handlePDFDownload}>
          <span className="btn-icon">📄</span> PDF
        </button>
        <button className="action-btn btn-excel" onClick={handleExportExcel}>
          <span className="btn-icon">📊</span> Excel
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Soo dejinaya...</span>
          </div>
          <p className="loading-text">Soo dejinaya xogta maaliyadeed...</p>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card gradient-green">
              <div className="stat-icon">💰</div>
              <div className="stat-details">
                <div className="stat-value">${totalStats.totalIncome.toFixed(2)}</div>
                <div className="stat-label">Wadarta Dakhliga</div>
              </div>
            </div>

            <div className="stat-card gradient-red">
              <div className="stat-icon">💸</div>
              <div className="stat-details">
                <div className="stat-value">${totalStats.totalExpenses.toFixed(2)}</div>
                <div className="stat-label">Wadarta Kharashaadka</div>
              </div>
            </div>

            <div className="stat-card gradient-yellow">
              <div className="stat-icon">👥</div>
              <div className="stat-details">
                <div className="stat-value">${totalStats.totalSalaries.toFixed(2)}</div>
                <div className="stat-label">Wadarta Mushaaraadka</div>
              </div>
            </div>

            <div className="stat-card gradient-blue">
              <div className="stat-icon">📈</div>
              <div className="stat-details">
                <div className="stat-value">${totalStats.netIncome.toFixed(2)}</div>
                <div className="stat-label">Dakhliga Saafi Ah</div>
              </div>
            </div>
          </div>

          {/* Charts */}
          {Object.keys(data).length > 0 && (
            <>
              <div className="charts-grid">
                <div className="chart-card">
                  <div className="card-header gradient-purple">
                    <span className="card-icon">📊</span>
                    <h3 className="card-title">Isbarbardhiga Dakhliga, Kharashaadka iyo Mushaaraadka</h3>
                  </div>
                  <div className="card-body">
                    <Bar
                      data={getIncomeExpenseChartData()}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              callback: (value) => `KSh ${value}`,
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                <div className="chart-card">
                  <div className="card-header gradient-indigo">
                    <span className="card-icon">📈</span>
                    <h3 className="card-title">Isbedelka Dakhliga Saafi Ah</h3>
                  </div>
                  <div className="card-body">
                    <Line
                      data={getNetIncomeChartData()}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              callback: (value) => `KSh ${value}`,
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="details-card">
                <div className="card-header gradient-orange">
                  <span className="card-icon">📋</span>
                  <h3 className="card-title">Faahfaahinta Bishii</h3>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="modern-table" id="report-table">
                      <thead>
                        <tr>
                          <th>Bisha</th>
                          <th>Wadarta Dakhliga</th>
                          <th>Wadarta Kharashaadka</th>
                          <th>Wadarta Mushaarka</th>
                          <th>Dakhliga Saafi Ah</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(data).map(([month, report]) => (
                          <tr key={month}>
                            <td className="fw-700">{month}</td>
                            <td className="text-success">${report.total_income}</td>
                            <td className="text-danger">${report.total_expenses}</td>
                            <td className="text-warning">${report.total_salaries}</td>
                            <td className="fw-700 text-primary">${report.net_income}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="total-row">
                          <td className="fw-700">Wadarta Guud</td>
                          <td className="text-success fw-700">${totalStats.totalIncome.toFixed(2)}</td>
                          <td className="text-danger fw-700">${totalStats.totalExpenses.toFixed(2)}</td>
                          <td className="text-warning fw-700">${totalStats.totalSalaries.toFixed(2)}</td>
                          <td className="text-primary fw-700">${totalStats.netIncome.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {Object.keys(data).length === 0 && (
            <div className="empty-state-card">
              <div className="empty-icon">📭</div>
              <h3>Lama Helin Xog</h3>
              <p>Lama helin diiwaan maaliyadeed oo u dhiganta shaandhaynta la doortay. Fadlan isku day inaad beddesho shuruudaha raadinta.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IncomeAndExpensesReport;
